'use client';

import { useEffect } from 'react';
import { MakeHero } from '@/new-cars/Hero';
import GeolocationModal from '@/new-cars/Modals/GeolocationModal';
import ParentModelSection from '@/new-cars/ParentModelSection';
import type { MakeConfigurationResponse } from '~/contracts/new-cars';
import useRecordNewCarsFlowVisit from '~/hooks/new-cars/useRecordNewCarsFlowVisit';
import useReportMissingImages from '~/hooks/new-cars/useReportMissingImages';
import useTestDriveCenter from '~/hooks/new-cars/useTestDriveCenter';
import { NEW_CARS_FLOW_LEVELS } from '~/utilities/new-cars/newCarsFlowLevel';
import TdcHeader from './Tdc/TdcHeader';

type OwnProps = {
  make: string;
  parentModels: MakeConfigurationResponse;
  showHero: boolean;
};

export default function MakeContainer({
  make,
  parentModels,
  showHero
}: OwnProps) {
  useRecordNewCarsFlowVisit(NEW_CARS_FLOW_LEVELS.make);
  useReportMissingImages({
    groups: [
      {
        items: parentModels.results.map(({ imageUrl, title }) => ({
          imageUrl,
          name: title
        })),
        section: 'parentModel'
      }
    ],
    isLoaded: true,
    vehicle: make
  });
  const testDriveCenter = useTestDriveCenter();
  const { sendCube } = testDriveCenter;

  // Model grid: tell the paired cube display to show its "select a model"
  // screen scoped to this make, carrying each model's inventory count (so the
  // display can render "X in stock") and this make's total (which the cube
  // accumulates per make to build its idle-screen fleet total). No-op when not
  // paired.
  useEffect(() => {
    sendCube({
      make,
      makeCount: parentModels.count,
      models: parentModels.results.map(result => ({
        count: result.count,
        model: result.next.parentModel
      })),
      screen: 'selectModel'
    });
  }, [make, parentModels, sendCube]);

  return (
    <div className="mx-auto flex min-h-screen max-w-[1800px] flex-col bg-background-weak">
      {testDriveCenter.isActive && <TdcHeader />}
      {showHero && <MakeHero make={make} />}
      <div className="mx-auto w-full max-w-[1056px]">
        <ParentModelSection make={make} parentModels={parentModels} />
      </div>
      <GeolocationModal />
    </div>
  );
}
